import {apiFetch} from '../../../security/apiFetch';
import {useState,useEffect,useCallback,useRef} from 'react';

export interface StoredAchievement {
 id:string;achievement_code:string;title:string;description:string|null;source:string;verification:string;
 progress:number|null;target:number|null;is_completed:boolean;unlocked_at:string|null;
}
export interface StoredChallenge {id:string;challenge_type:string;date:string;is_completed:boolean;completed_count:number;verification:string;reward_points:number;}
interface UserStats {lessons_completed:number;days_streak:number;terms_mastered:number;letters_learned:number;time_spent:number;}

export const useAchievements=(token:string|null)=>{
 const [achievements,setAchievements]=useState<StoredAchievement[]>([]),[dailyTasks,setDailyTasks]=useState<StoredChallenge[]>([]);
 const [userStats,setUserStats]=useState<UserStats|null>(null),[isLoading,setLoading]=useState(false),[error,setError]=useState<string|null>(null);
 const inFlight=useRef(new Map<string,Promise<void>>()),generation=useRef(0);
 const reload=useCallback(async(signal?:AbortSignal)=>{
  const current=++generation.current;
  setLoading(true);setError(null);setAchievements([]);setDailyTasks([]);setUserStats(null);
  try{
   if(!token)throw new Error();
   const options={headers:{Authorization:`Bearer ${token}`},signal};
   const responses=await Promise.all([apiFetch('/api/v1/arabic-lessons/user/stats',options),apiFetch('/api/v1/arabic-lessons/user/achievements',options),apiFetch('/api/v1/arabic-lessons/daily-challenge',options)]);
   if(responses.some(response=>!response.ok))throw new Error();
   const [stats,awards,challenge]=await Promise.all(responses.map(response=>response.json()));
   if(!Array.isArray(awards)||awards.some(item=>typeof item.id!=='string'||typeof item.title!=='string'||typeof item.is_completed!=='boolean')||!Array.isArray(stats.letters_learned)||!Number.isFinite(stats.total_lessons_completed)||!Number.isFinite(stats.total_time_spent))throw new Error();
   if(challenge.challenge!==null&&(!challenge.id||typeof challenge.is_completed!=='boolean'))throw new Error();
   if(!signal?.aborted&&current===generation.current){
    setAchievements(awards);setDailyTasks(challenge.challenge===null?[]:[challenge]);
    setUserStats({lessons_completed:stats.total_lessons_completed,days_streak:stats.current_streak,
     letters_learned:stats.letters_learned.length,time_spent:Math.floor(stats.total_time_spent/60),terms_mastered:0});
   }
  }catch{
   if(!signal?.aborted&&current===generation.current)setError('Достижения и статистика недоступны. Повторите загрузку.');
  }finally{if(!signal?.aborted&&current===generation.current)setLoading(false);}
 },[token]);
 useEffect(()=>{const controller=new AbortController(),version=generation;void reload(controller.signal);return()=>{controller.abort();version.current++;};},[reload]);
 const completeTask=useCallback((id:string)=>{
  const current=generation.current,key=current+':'+id;
  const pending=inFlight.current.get(key);if(pending)return pending;
  const task=dailyTasks.find(item=>item.id===id);
  if(!token||!task||task.is_completed)return Promise.resolve();
  setLoading(true);
  const operation=(async()=>{
   try{
    const response=await apiFetch('/api/v1/arabic-lessons/daily-challenge/'+encodeURIComponent(id)+'/complete',{method:'POST',headers:{Authorization:`Bearer ${token}`}});
    if(!response.ok)throw new Error();
    const result=await response.json();if(result.is_completed!==true||result.verification!=='self_reported')throw new Error();
    if(current===generation.current)await reload();
   }catch{if(current===generation.current)setError('Выполнение задания не подтверждено сервером.');}
   finally{inFlight.current.delete(key);if(current===generation.current)setLoading(false);}
  })();inFlight.current.set(key,operation);return operation;
 },[token,dailyTasks,reload]);
 return {achievements,dailyTasks,userStats,isLoading,error,reload,completeTask};
};
